import mapValues from 'lodash/mapValues';
import * as yup from 'yup';
import { FontIconKey, iconMapping } from '../../components/FontIcon';
import * as icons from '../../theme/icons';
import { IconKey } from '../../types';
import { keys } from '../../utils/Object';

import {
  ContentType,
  CType,
  DokType,
  Employee,
  Faq,
  GridTeaserPage,
  MenuEntry,
  MetaBase,
  Page,
  TextAlign,
  Typo3Files,
  Typo3Image,
  Typo3Menu,
  Typo3PathContentResponse,
  Typo3RequestInfo,
  Typo3StaticContentResponse,
  VehicleCenter
} from './types';

// type ObjectShape<T extends object> = yup.ObjectSchema<yup.Shape<{}, T>>;
type ObjectShape<T extends object> = any | T; // FIXME
type Schema<T> = any | T; // FIXME

export const typo3ImageSchema: ObjectShape<Typo3Image> = yup.object({
  uid: yup
    .number()
    .default(0)
    .required(),
  identifier: yup
    .string()
    .default('')
    .required(),
  extension: yup
    .string()
    .default('')
    .required(),
  name: yup
    .string()
    .default('')
    .required(),
  imageUri: yup
    .string()
    .default('')
    .required(),
  imageZoomUri: yup.string().default('')
});

export const isStringOrInteger = (value: any) =>
  typeof value === 'string' || Number.isInteger(value);

export const stringOrIntegerSchema: Schema<DokType> = yup
  .mixed()
  .default(0)
  // if value is no string or integer, transform value to `undefined`
  // so that can be defaulted to the value set by `default()`
  .transform(value => (isStringOrInteger(value) ? value : undefined))
  .test({
    name: 'StringOrInteger',
    message: `\${path} must be number (integer) or string`,
    test: isStringOrInteger
  });

export const pageSchema: ObjectShape<Page> = yup.object({
  uid: yup.number().default(0),
  pid: yup.number().default(0),
  tstamp: yup.number().default(0),
  doktype: stringOrIntegerSchema,
  title: yup.string().default(''),
  description: yup
    .string()
    .nullable()
    .default(''),
  link: yup.string().default(''),
  seoTitle: yup.string().default(''),
  keywords: yup.array().of(yup.string()).default([]),
  facebookTitle: yup
    .string()
    .nullable()
    .default(''),
  facebookDescription: yup
    .string()
    .nullable()
    .default(''),
  facebookImage: typo3ImageSchema.default(undefined),
  twitterCreator: yup
    .string()
    .nullable()
    .default(''),
  twitterDescription: yup
    .string()
    .nullable()
    .default(''),
  twitterImage: typo3ImageSchema.default(undefined),
  twitterTitle: yup
    .string()
    .nullable()
    .default('')
});

export const gridTeaserPageSchema: ObjectShape<GridTeaserPage> = yup.object({
  uid: yup
    .number()
    .default(0)
    .required(),
  title: yup
    .string()
    .default('')
    .required(),
  subtitle: yup
    .string()
    .default('')
    .required(),
  bodytext: yup
    .string()
    .default('')
    .required(),
  linkLabel: yup
    .string()
    .default('')
    .required(),
  link: yup.string().default(undefined),
  image: typo3ImageSchema.default(undefined),
  magazine_teaser: typo3ImageSchema.default(undefined),
  magazine_teaser_top: typo3ImageSchema.default(undefined)
});

export const typo3FilesSchema: ObjectShape<Typo3Files> = yup.object({
  image: yup
    .array()
    .of(typo3ImageSchema)
    .default(undefined),
  image_mobile: yup
    .array()
    .of(typo3ImageSchema)
    .default(undefined),
  assets: yup
    .array()
    .of(typo3ImageSchema)
    .default(undefined)
});

const isOneOrZero = (value: any) => value === 1 || value === 0;

const oneOrZeroSchema: Schema<0 | 1> = yup
  .mixed()
  .test({
    name: 'OneOrZero',
    message: `\${path} must be 1 or 0.`,
    test: isOneOrZero
  })
  .default(0);

const iconKeys: IconKey[] = keys(icons);
const fontIconKeys: FontIconKey[] = keys(iconMapping);

const isIcon = (value: any) => {
  return (
    value === '' || iconKeys.includes(value) || fontIconKeys.includes(value)
  );
};

const iconSchema: Schema<IconKey | FontIconKey> = yup
  .mixed()
  .default(undefined)
  .test({
    name: 'Icon',
    message: `\${path} must be one of the following: ${iconKeys.join(
      ', '
    )}, ${fontIconKeys.join(', ')}`,
    test: isIcon
  });

export const menuEntrySchema: ObjectShape<MenuEntry> = yup.object({
  title: yup.string(),
  link: yup.string(),
  active: oneOrZeroSchema,
  available: oneOrZeroSchema.notRequired(),
  current: oneOrZeroSchema,
  children: yup.array().of(yup.lazy(_ => menuEntrySchema) as any /* FIXME */),
  languageUid: yup.string(),
  icon: iconSchema,
  layout: stringOrIntegerSchema,
  locale: yup.string(),
  uid: yup.number()
});

const contentTypes: ContentType[] = [
  'busoffers',
  'bustypeselect',
  'calltoaction',
  'calltoaction_button',
  'div',
  'form_contact',
  'form_eventregistration',
  'form_sell',
  'video_full',
  'video_teaser',
  'grid_teaser',
  'grid10years',
  'header',
  'html',
  'image_content',
  'image_full',
  'magazine_overview',
  'menu_pages',
  'newsletter_form',
  'search',
  'searchdetail',
  'searchlocation',
  'searchmanufacturer',
  'slider_pic',
  'slider_textpic',
  'sitemap',
  'teaser_circle',
  'teaser_slider_magazine',
  'teaser_square',
  'text',
  'textpic_left_33',
  'textpic_right_33',
  'textpic_left_50',
  'textpic_right_50',
  'textvideo_left_50',
  'textvideo_right_50',
  'twoimages',
  'usedvehiclecenter_map',
  'usercentrics_privacy',
  'watchlist',
  'ppi_newsletterteaser'
];

const isContentType = (value: any) => {
  return contentTypes.includes(value);
};

export const contentTypeSchema: Schema<ContentType> = yup.mixed().test({
  name: 'ContentType',
  message: `\${path} must be one of the following: ${contentTypes.join(', ')}`,
  test: isContentType
});

const textAlignValues: TextAlign[] = ['left', 'center', 'right', 'justify'];

export const ctypeSchema: ObjectShape<CType> = yup.object({
  uid: yup.number(),
  pid: yup.number(),
  CType: contentTypeSchema,
  tstamp: yup.number(),
  header: yup.string(),
  header_link: yup.string(),
  subheader: yup.string(),
  button: yup.string(),
  pages: yup.array(),
  bodytext: yup.string(),
  files: typo3FilesSchema,
  layout: stringOrIntegerSchema,
  navigation_enable: yup.number(),
  header_position: yup.string().is(textAlignValues),
  bodytext_position: yup.string().is(textAlignValues),
  bodytext_position_mobile: yup.string().is(textAlignValues),
  date1: yup.number(),
  date2: yup.number(),
  event_id: yup.string(),
  content_element_width: yup.string().is(['text-width']),
});

export const employeeSchema: ObjectShape<Employee> = yup.object({
  uid: yup.number(),
  name: yup.string(),
  position: yup.string(),
  position_label: yup.string(),
  phone: yup.string(),
  mobile: yup.string(),
  primary_phone: yup.mixed(),
  fax: yup.string(),
  email: yup.string(),
  linguisticProficiency: yup.string(),
  linguisticProficiency_labels: yup.array().of(yup.string()),
  image: typo3ImageSchema.default(undefined)
});

export const vehicleCenterSchema: ObjectShape<VehicleCenter> = yup.object({
  uid: yup.number(),
  name: yup.string(),
  nameWithoutPrefix: yup.string(),
  company: yup.string(),
  country: yup.string(),
  countryCode: yup.string(),
  directionsNote: yup.string().nullable(),
  searchValue: yup.string(),
  street: yup.string(),
  zip: yup.string(),
  city: yup.string(),
  phone: yup.string(),
  fax: yup.string(),
  email: yup.string(),
  businessHours: yup.string(),
  showInMap: yup.boolean(),
  isSalesOffice: yup.boolean(),
  googlePlacesUrl: yup.string(),
  latitude: yup.string(),
  longitude: yup.string(),
  link: yup.string(),
  image: typo3ImageSchema,
  employees: yup.array().of(employeeSchema),
  monday: yup.string(),
  tuesday: yup.string(),
  wednesday: yup.string(),
  thursday: yup.string(),
  friday: yup.string(),
  saturday: yup.string(),
  sunday: yup.string(),
  isMainLocationInCountry: yup.boolean()
});

export const faqSchema: ObjectShape<Faq> = yup.object({
  uid: yup.number(),
  question: yup.string(),
  answer: yup.string(),
  categories: yup.array()
});

export const metaBaseSchema: ObjectShape<MetaBase> = yup.object({
  page: pageSchema,
  locale_all: yup.string(),
  locale: yup.string(),
  doktype: stringOrIntegerSchema,
  sys_language_uid: yup.number(),
  sys_language_isocode: yup.string(),
  countrycode: yup.string(),
  matomoSiteId: yup.string(),
  matomoConsentlessSiteId: yup.string(),
  storyboardId: yup.string(),
  vehicleSlugsFile: yup.string(),
});

const oneOrZeroStringSchema = yup
  .mixed()
  .default('0')
  .test({
    name: 'OneOrZeroString',
    message: `\${path} must be "1" or "0"`,
    test: (value: any) => {
      return value === '1' || value === '0';
    }
  });

export const requestInfoSchema: ObjectShape<Typo3RequestInfo> = yup.object({
  content: oneOrZeroStringSchema,
  meta: oneOrZeroStringSchema,
  menu: oneOrZeroStringSchema,
  cookie: oneOrZeroStringSchema,
  labels: oneOrZeroStringSchema,
  layer: oneOrZeroStringSchema,
  faqs: oneOrZeroStringSchema,
  redirects: oneOrZeroStringSchema,
  usedVehicleCenters: oneOrZeroStringSchema,
  id: yup.string().nullable(),
  type: yup.mixed()/* .test({
    name: 'Type110',
    message: `\${path} must be "110".`,
    test: (value: any) => value === '110'
  }) */,
});

export const staticContentSchema: ObjectShape<
  Typo3StaticContentResponse
> = yup.object({
  layer_left: yup.array().of(ctypeSchema),
  layer_middle: yup.array().of(ctypeSchema),
  layer_right: yup.array().of(ctypeSchema),
  labels: yup.object({
    data: yup.array().of(
      yup.object({
        clue: yup.string(),
        value: yup.string()
      })
    ),
    usedVehicles: yup.mixed(),
  }),
  meta: metaBaseSchema.shape({
    usedVehicleCenters: yup.array().of(vehicleCenterSchema)
  }),
  request: requestInfoSchema
});

export const pathContentSchema: ObjectShape<
  Typo3PathContentResponse
> = yup.object({
  request: requestInfoSchema,
  content: yup.array().of(ctypeSchema),
  stage: yup.array().of(ctypeSchema),
  meta: metaBaseSchema.shape({
    faqs: yup.array().of(faqSchema)
  }),
  cookie: yup.array().of(ctypeSchema),
  menu: yup.lazy((menu: Typo3Menu) =>
    yup.object(mapValues(menu, () => yup.array().of(menuEntrySchema)))
  )
});
